import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Box, Button, Card, Stack, Menu, MenuItem, Typography } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import EastOutlinedIcon from "@mui/icons-material/EastOutlined";
import WestOutlinedIcon from "@mui/icons-material/WestOutlined";
import { AuthenticationContext } from "@cloudmlm/cloudmlm-auth";
import urlConstant from "../../constants/urlConstants";
import dataConstant from "../../constants/dataConstants";
import { httpGet, httpPost } from "../../services/commonService";
import MediaCardGrid from "./mediaDataGrid";

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const MediaGallery = ({ media, onBack }) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [value, setValue] = useState(0);
  const scrollContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const { accessToken, impersonateEmail } = useContext(AuthenticationContext);
  const url = `${urlConstant.MediaPage.GetAllMedialMenuById}?Id=${atob(id)}`;
  const [mediaMenuTabs, setMediaMenuTabs] = useState([]);
  const [selectedMenuId, setSelectedMenuId] = useState(media.mediaId);
  const [selectedTabId, setSelectedTabId] = useState(media.id);
  const [anchorElTab, setAnchorElTab] = useState(null);
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const scrollStartX = useRef(0);

  const handleMouseDown = (e) => {
    isDragging.current = true;
    dragStartX.current = e.clientX;
    scrollStartX.current = scrollContainerRef.current.scrollLeft;    
    document.body.style.userSelect = "none";
  };

  const handleMouseMove = (e) => {
    if (!isDragging.current) return;
    const dx = e.clientX - dragStartX.current;
    scrollContainerRef.current.scrollLeft = scrollStartX.current - dx;
    checkScrollButtons();
  };

  const handleMouseUp = () => {
    isDragging.current = false;
    document.body.style.userSelect = "";
  };

  useEffect(() => {
    if (!scrollContainerRef.current) return;
    const el = scrollContainerRef.current;
    el.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      el.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  const handleChange = (event, newValue) => {
    setValue(newValue);
    setSelectedMenuId(mediaMenuTabs[newValue]?.MediaId);
    setSelectedTabId(mediaMenuTabs[newValue]?.Id)
  };
  const mediaTypeMap = {
    [dataConstant.MediaCategoryTypes.Picture]: t("Media.Images"),
    [dataConstant.MediaCategoryTypes.Pdf]: t("Media.PDF"),
    [dataConstant.MediaCategoryTypes.Video]: t("Media.Videos"),
    [dataConstant.MediaCategoryTypes.TextBlog]: t("Media.TextBlog"),
  };
  const pageTitle = mediaTypeMap[media.type] || t("Media.Details");
  const backToMediaCategories = () => {
      onBack(selectedTabId, media.mediaId, media.type);
  };

  const handleDownloadURL = async (url) => {
    window.open(`${url}`);
  };
  const gridButtons = [
    {
      id: "download",
      onClick: handleDownloadURL,
    },
  ];

  const handleTabClick = (event) => {
    setAnchorElTab(event.currentTarget);
  };

  const handleTabClose = (index) => {
    setAnchorElTab(null);
    if (typeof index === "number") {
      setValue(index);
      setSelectedMenuId(mediaMenuTabs[index]?.MediaId);
      setSelectedTabId(mediaMenuTabs[index]?.Id);
    }    
  };

  useEffect(() => {
    getResources(media.id);
  }, []);

  const fetchDataFunction = async ({
    page,
    pageSize,
    search,
    sortKey,
    sortDirection,
  }) => {
    const pagingInfo = {
      PageNumber: page,
      RowsPerPage: pageSize,
      CommonSearchText: search,

      SortColumns: {
        SortColumnName: sortKey,
        SortOrder: sortDirection,
      },
      Filter: {},
    };
    const requestBody = {
      MediaType: media.type,
      MediaPageID: selectedMenuId,
      PagingInfoVM: pagingInfo,
    };
    try {
      const response = await httpPost(
        urlConstant.MediaPage.GetMediaGalleryByMenuId,
        requestBody,
        true,
        false,
        accessToken,
        impersonateEmail
      );
      if (response?.Value?.Status === dataConstant.APIResponseStatus.Success) {
        return {
          data: response.Value.Data.Data,
          totalCount: response.Value.Data.TotalCount,
        };
      } else {
        return { data: [], totalCount: 0 };
      }
    } catch (err) {
      console.error(err);
    }
  };

  const getResources = async (selectedMediaId) => {
    await httpGet(url, true, true, accessToken, impersonateEmail)
      .then((response) => {
        const responseData = response.Value.Data;        
        setMediaMenuTabs(responseData);
        const index = responseData.findIndex(
          (item) => item.Id === selectedMediaId
        );
        if (index !== -1) {
          setValue(index);
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };
  const checkScrollButtons = () => {
    const el = scrollContainerRef.current;
    if (!el) return;

    const scrollLeft = Math.ceil(el.scrollLeft);
    const scrollWidth = Math.ceil(el.scrollWidth);
    const clientWidth = Math.ceil(el.clientWidth);

    setCanScrollLeft(scrollLeft > 0);
    const nearEnd = scrollLeft + clientWidth >= scrollWidth - 1;
    setCanScrollRight(!nearEnd);
  };

  const handleScroll = (direction) => {
    const el = scrollContainerRef.current;
    if (!el) return;

    const scrollAmount = 200;
    el.scrollBy({
      left: direction === "right" ? scrollAmount : -scrollAmount,
      behavior: "smooth",
    });

    // Snap if near right end
      setTimeout(() => {
          if (el.scrollLeft + el.clientWidth >= el.scrollWidth - 2) {
              el.scrollLeft = el.scrollWidth;
          }
          checkScrollButtons();
      }, 500);
  };

  const tabRefs = useRef([]);
  useEffect(() => {
    const scrollEl = scrollContainerRef.current;
    const tabEl = tabRefs.current[value];

    if (scrollEl && tabEl) {
      const tabLeft = tabEl.offsetLeft;
      const tabRight = tabLeft + tabEl.offsetWidth;
      const visibleLeft = scrollEl.scrollLeft;
      const visibleRight = visibleLeft + scrollEl.clientWidth;

      // Scroll left if tab is cut on the left
      if (tabLeft < visibleLeft) {
        scrollEl.scrollTo({
          left: tabLeft,
          behavior: "smooth",
        });
      }
      // Scroll right if tab is cut on the right
      else if (tabRight > visibleRight) {
        scrollEl.scrollTo({
          left: tabRight - scrollEl.clientWidth + tabLeft / 2, // add small padding
          behavior: "smooth",
        });
      }
        // Re-check after animation
        setTimeout(checkScrollButtons, 500);
    }
  }, [value, mediaMenuTabs]);

  const getPdfFileLink = (id) => {
    fetch(`${urlConstant.MediaPageDetails.GetMediaPagePdfFile}?Id=${id}`, {
      responseType: "blob",
      method: "get",
      headers: {
        "Content-Type": "application/json",
        impersonateEmail: impersonateEmail,
        Authorization: `bearer ${accessToken}`,
      },
    })
      .then((response) => {
        response.blob().then((data) => {
          if (data.size > 0) {
            window.open(window.URL.createObjectURL(data));
          }
        });
      })
      .catch((err) => {
        console.error(err);
      });
  };
  const pdfButtons = [
    {
      id: "view-details",
      label: "Edit",
      icon: "pencil",
      color: "secondary-main",
      onClick: getPdfFileLink,
    },
  ];
  return (
    <>
      <Box flexDirection={"column"} gap={2} display={"flex"}>
        <Box width={"100%"}>
          <Card
            sx={(theme) => ({
              p: {
                xs: "20px",
                sm: "25px 30px",
              },
              background: theme.palette.background.cardGradient,
              borderRadius: "20px",
              height: "100%",
            })}
          >            
            <Stack gap={"20px"}>              
              <Stack
                direction={"row"}
                alignItems={"center"}
                width={"100%"}
                gap={"10px"}
                position={"relative"}
                sx={{ display: { xs: "flex", sm: "none" } }}
                mb={1}
                justifyContent="space-between"
              > 
                {mediaMenuTabs[value] && (
                  <Typography
                    variant="h5"
                    sx={{
                      ml: 1,
                      fontWeight: 500,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: 500,
                    }}
                  >
                    {mediaMenuTabs[value].Title}
                  </Typography>
                )}
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleTabClick}
                  sx={{
                    px: 0.8,
                    py: 1,
                    minWidth: "40px",
                    minHeight: "40px",
                    ml: "auto",
                  }}                  
                >
                  <MoreVertIcon />
                </Button>
              </Stack>
              <Stack className="tabs-common secondary-design"
                sx={{ display: { xs: "none", sm: "flex" } }}>
                <Stack
                  direction={"row"}
                  alignItems={"center"}
                  width={"100%"}
                  gap={"10px"}
                  position={"relative"}
                >
                  {canScrollLeft && (
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={() => handleScroll("left")}
                      sx={{
                        px: 2,
                        py: 2,                        
                        margin: 0,
                        "& .MuiButton-startIcon": {
                          margin: 0,
                        },
                        display: { md: "flex" },
                      }}
                      startIcon={<WestOutlinedIcon />}
                    />
                  )}
                  <Box
                    ref={scrollContainerRef}
                    sx={{
                      display: {                        
                        md: "flex",
                        overflow: "auto",
                        width: "calc(100% - 60px)",
                        scrollbarWidth: "none",
                      },

                      width: "100%",
                      scrollbarWidth: "none",
                      " &::-webkit-scrollbar": {
                        display: "none",
                      },
                    }}
                    style={{ cursor: "grab" }}
                  >
                    <Tabs
                      value={value}
                      onChange={handleChange}
                      aria-label="media tabs"
                      variant="scrollable"
                      sx={{
                        minWidth: "max-content",
                      }}
                    >
                      {mediaMenuTabs.map((tab, index) => (
                        <Tab
                          key={tab.Id}
                          label={tab.Title}
                          sx={{ maxWidth: "fit-content" }}
                          {...a11yProps(index)}
                          ref={(el) => (tabRefs.current[index] = el)}
                        />
                      ))}
                    </Tabs>
                  </Box>
                  {canScrollRight && (
                    <Button
                      variant="contained"
                      color="secondary"
                      sx={{
                        px: 2,
                        py: 2,
                        margin: 0,
                        ml: "auto",
                        "& .MuiButton-startIcon": {
                          margin: 0,
                        },
                        display: { md: "flex" },
                      }}
                      onClick={() => handleScroll("right")}
                      startIcon={<EastOutlinedIcon />}
                    />
                  )}
                </Stack>
              </Stack>
              <Stack
                direction={"row"}
                gap={"20px"}
                width={"100%"}
                alignItems={"center"}
                justifyContent={"space-between"}
                flexWrap={"wrap"}
                key="MediaGellery"
              >
                <MediaCardGrid
                  key="MediaGrid"
                  onFetchData={fetchDataFunction}
                  defaultItemsPerPage={9}
                  title={pageTitle}
                  actionButtons={gridButtons}
                  value={mediaMenuTabs[value]?.MediaId}
                  mediaType={media.type}
                  pdfButtons={pdfButtons}
                  onBack={backToMediaCategories}
                />
              </Stack>
            </Stack>
          </Card>
        </Box>
      </Box>
      <Menu
        anchorEl={anchorElTab}
        open={Boolean(anchorElTab)}
        onClose={() => handleTabClose()}
        sx={{ display: { xs: "block", sm: "none" } }}
        PaperProps={{
          sx: {
            maxHeight: 320,
            width: 250,
            overflowY: "auto",
          },
        }}        
      >
        {mediaMenuTabs.map((tab, index) => (
          <MenuItem
            key={tab.Id}
            selected={index === value}
            onClick={() => handleTabClose(index)}
          >
            {tab.Title}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
export default MediaGallery;